<template>
	<div class="chat-log h-100 d-flex flex-column overflow-hidden">
		<div class="d-flex flex-fill justify-content-center align-items-center" v-if="loading > 0">
			<b-icon icon="circle-fill" animation="throb" font-scale="2"></b-icon>
		</div>
		<div v-show="loading <= 0" ref="alertLog" class="flex-fill overflow-auto dashboardChatLog" @scroll="onScroll($event)"></div>
		<div class="chatLogScrollToBottom" v-show="historyMode">
			<b-button @click="bottomOut()"><b-icon icon="arrow-down"></b-icon></b-button>
		</div>
		<div class="alert-log-footer p-2 d-flex align-items-center">
			<b-button size="sm" :variant="stopped ? 'success' : 'danger'" class="mr-2" @click="toggleStopped()">
				<b-icon :icon="stopped ? 'play-fill' : 'stop-fill'"></b-icon>
				{{stopped ? 'Start' : 'Stop'}}
			</b-button>
			<b-button size="sm" variant="secondary" class="mr-2" @click="togglePause()" :disabled="stopped">
				<b-icon :icon="paused ? 'play-fill' : 'pause-fill'"></b-icon>
				{{paused ? 'Resume' : 'Pause'}}
			</b-button>
			<b-button size="sm" variant="secondary" @click="skip()">
				<b-icon icon="skip-end-fill"></b-icon>
				Skip
			</b-button>
			<b-dropdown size="sm" variant="secondary" text="Test" class="ml-auto" right dropup>
				<b-dropdown-item-button v-for="a in alertTypes" :key="a.event_type"
					:disabled="a.status === 'disabled'"
					@click="playTestAlert(a.event_type)">
					{{a.event_name}}
				</b-dropdown-item-button>
				<b-dropdown-divider v-if="alertTypes.length"></b-dropdown-divider>
				<b-dropdown-item-button @click="playTestAlert('gift', { below: giftThresholds.length > 0 ? giftThresholds[0].amount : null, currency: user && user.currency_code })">
					Gift from $0
				</b-dropdown-item-button>
				<b-dropdown-item-button v-for="(t, idx) in giftThresholds" :key="t.id"
					@click="playTestAlert('gift', { threshold: t.amount, below: idx < giftThresholds.length - 1 ? giftThresholds[idx+1].amount : null, currency: user && user.currency_code })">
					Gift from {{user && generateValueString(t.amount, user.currency_code)}}
				</b-dropdown-item-button>
			</b-dropdown>
		</div>
		<message-details-overlay
			:message="selectedMessage"
			:user="user"
			:serviceIcons="serviceIcons"
			@close="selectedMessage = null"
			@select="highlightMessage($event)"
			@replay="replayAlert($event)"
		/>
	</div>
</template>

<script>
import util from '@/util';
import giftColorThresholds from '@/GiftThresholds';
import MessageDetailsOverlay from '@/components/MessageDetailsOverlay.vue';

export default {
	components: { MessageDetailsOverlay },
	props: {
		serviceIcons: Object,
	},
	data() { return {
		loading: 0,
		paused: false,
		stopped: false,
		historyMode: false,
		user: null,
		giftTypes: {},
		giftThresholds: [],
		alertTypes: [],
		selectedMessage: null,
	}},
	created() {
		this.scrollPos = 0;
		this.prevHeight = 0;
		this.prevScrollHeight = 0;
	},
	async mounted() {
		this._injectGiftColors();
		try {
			this.loading++;
			this.startScrollAnimation();
			this.user = await this.$api.get('userData');
			const [alerts, giftTypesResult, giftSettings, giftThresholds, eventTypes] = await Promise.all([
				this.$api.get('alerts'),
				this.$api.get('userGiftTypes'),
				this.$api.get('widgetSettings/gift'),
				this.$api.get('giftThresholds'),
				this.$api.get('userEventTypes'),
			]);
			this.stopped = !!giftSettings?.stopped;
			this.giftThresholds = giftThresholds || [];
			this.alertTypes = (eventTypes || []).filter(t => t.event_alert_type == 'alert' && t.event_type != 'gift' && t.event_type != 'alert');
			this.loadGiftTypes(giftTypesResult.results);
			for(const alert of alerts)
				this.appendAlert(alert.message, alert.status, alert.id);
			if(this.$refs.alertLog)
				this.$refs.alertLog.scrollTop = this.$refs.alertLog.scrollHeight;
			this.subId = this.$socket.subscribe(this.user.widget_hash, {
				userWidgetAlert: msg => {
					if(msg.sent_at) return;
					if(msg.type == 'tower') return;
					if(!this.historyMode) this.appendAlert(msg.message, 'queued', msg.id);
				},
				userWidgetAlertStatus: msg => {
					const found = document.querySelector(`[data-alert-id="${CSS.escape(msg.alertId)}"]`);
					if(found) this._setStatusBadge(found, msg.status);
				},
				widgetSettingsFieldChange: msg => {
					if(msg.widgetType === 'gift' && msg.field === 'stopped') {
						this.stopped = !!msg.value;
					}
				},
				messageHighlighted: msg => {
					const log = this.$refs.alertLog;
					if(log) for(const h of Array.from(log.getElementsByClassName('highlighted'))) h.classList.remove('highlighted');
					this.user.highlighted_message = msg;
					document.getElementById(msg.id)?.classList.add('highlighted');
				},
				messageUnhighlighted: () => {
					const log = this.$refs.alertLog;
					if(log) for(const h of Array.from(log.getElementsByClassName('highlighted'))) h.classList.remove('highlighted');
					this.user.highlighted_message = null;
				},
			});
		} catch(e) {
			console.error(e);
		} finally {
			this.loading--;
		}
	},
	beforeDestroy() {
		this.stopScrollAnimation();
		this.subId && this.$socket.unsubscribe(this.subId);
	},
	methods: {
		_injectGiftColors() {
			if(document.getElementById('alert-log-gift-colors')) return;
			const style = document.createElement('style');
			style.id = 'alert-log-gift-colors';
			style.textContent = giftColorThresholds.map(t => `
				.dashboardChatLog > .message.gift.gift-${t.value}:not(.highlighted) {
					background-color: ${t.color};
					color: ${util.getContrastingTextColor(t.color)};
				}
				.dashboardChatLog > .message.gift.gift-${t.value}:not(.highlighted):hover {
					background-color: color-mix(in srgb, ${t.color} 90%, #000);
				}
			`).join('');
			document.head.appendChild(style);
		},
		loadGiftTypes(giftTypes) {
			this.giftTypes = {};
			for(const giftType of giftTypes) {
				if(!(giftType.service_id in this.giftTypes))
					this.giftTypes[giftType.service_id] = {};
				this.giftTypes[giftType.service_id][giftType.gift_type] = giftType;
			}
		},
		_setStatusBadge(elem, status) {
			let badge = elem.querySelector('.alert-status-badge');
			if(!status || status === 'queued') {
				badge?.remove();
				return;
			}
			if(!badge) {
				badge = document.createElement('span');
				badge.className = 'alert-status-badge';
				elem.appendChild(badge);
			}
			const labels = { playing: '▶ playing', played: '✓ played', error: '⚠ error', paused: '⏸ paused', skipped: '⏭ skipped', ignored: '⊘ ignored' };
			badge.textContent = labels[status] || status;
			badge.dataset.status = status;
		},
		appendAlert(msg, status, id) {
			if(!this.$refs.alertLog) return;
			const elem = util.createChatLogMessage(msg, {
				serviceIcons: this.serviceIcons,
				giftColorThresholds,
				user: this.user,
				giftTypes: this.giftTypes,
			}, {
				onclick: prepared => { this.selectedMessage = prepared; },
				onmenuclick: prepared => this.highlightMessage(prepared),
			});
			if(id) elem.dataset.alertId = id;
			this._setStatusBadge(elem, status);
			this.$refs.alertLog.appendChild(elem);
		},
		startScrollAnimation() {
			if(this._animationId) return;
			const frame = () => {
				this._animationId = window.requestAnimationFrame(frame);
				if(!this.$refs.alertLog || this.historyMode) return;
				const el = this.$refs.alertLog;
				const diff = el.scrollHeight - el.offsetHeight - el.scrollTop;
				if(diff > 400) {
					el.scrollTop += diff;
				} else {
					el.scrollTop += Math.round(diff / 5) + 1;
				}
			};
			this._animationId = window.requestAnimationFrame(frame);
		},
		stopScrollAnimation() {
			if(this._animationId) {
				window.cancelAnimationFrame(this._animationId);
				delete this._animationId;
			}
		},
		onScroll(event) {
			const el = event.target;
			const scrollBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
			if(this.historyMode) {
				if(scrollBottom < 1) this.historyMode = false;
			} else {
				const scrolledPx = el.scrollTop - this.scrollPos;
				const resizedPx = el.clientHeight - this.prevHeight;
				const innerPx = el.scrollHeight - this.prevScrollHeight;
				if(innerPx === 0 && resizedPx === 0 && scrolledPx < 0)
					this.historyMode = true;
			}
			this.scrollPos = el.scrollTop;
			this.prevHeight = el.clientHeight;
			this.prevScrollHeight = el.scrollHeight;
		},
		bottomOut() {
			this.historyMode = false;
			if(this.$refs.alertLog)
				this.$refs.alertLog.scrollTop = this.$refs.alertLog.scrollHeight;
		},
		highlightMessage(msg) {
			const target = document.getElementById(msg.id);
			target?.classList.add('loading');
			const is_current = String(msg.serviceId) == String(this.user?.highlighted_message?.serviceId) && msg.token == this.user?.highlighted_message?.token;
			this.$api.post('selectMessage', is_current ? null : msg).then(() => {
				const highlighted = this.$refs.alertLog?.getElementsByClassName('highlighted');
				if(highlighted) for(const h of highlighted) h.classList.remove('highlighted');
				if(is_current) {
					this.user.highlighted_message = null;
				} else {
					target?.classList.add('highlighted');
					this.user.highlighted_message = msg;
				}
			}).catch(err => {
				this.$bvToast.toast('Could not select message: ' + (err.error || err.toString()), {
					title: 'Error',
					variant: 'danger',
					solid: true,
				});
			}).finally(() => {
				target?.classList.remove('loading');
			});
			this.$broadcastChannel?.postMessage({
				type: 'highlightedMessage',
				data: is_current ? null : JSON.parse(JSON.stringify(msg)),
			});
		},
		replayAlert(msg) {
			this.$api.post('alerts/replay', { serviceId: msg.serviceId, token: msg.token });
		},
		async toggleStopped() {
			try {
				await this.$api.post('widgetSettings/gift/stopped', { value: !this.stopped });
				// stopped state updates reactively via widgetSettingsFieldChange socket event
			} catch(e) {
				console.error(e);
			}
		},
		async togglePause() {
			try {
				if(this.paused) {
					await this.$api.post('alerts/queue/resume');
					this.paused = false;
				} else {
					await this.$api.post('alerts/queue/pause');
					this.paused = true;
				}
			} catch(e) {
				console.error(e);
			}
		},
		async skip() {
			try {
				await this.$api.post('alerts/skip');
			} catch(e) {
				console.error(e);
			}
		},
		async playTestAlert(type, data) {
			try {
				await this.$api.post('alerts/test', { type, data });
			} catch(e) {
				console.error(e);
				this.$bvToast.toast('Could not send test alert: ' + (e.error || String(e)), {
					title: 'Error', variant: 'danger', solid: true,
				});
			}
		},
		generateValueString(value, currency) {
			return util.generateValueString(value, currency);
		},
	},
};
</script>

<style lang="scss">
@import '../plugins/variable.scss';

/* Differentiate the controls footer with a slightly lighter background instead of a border. */
.alert-log-footer {
	background-color: rgba(255, 255, 255, 0.05);
}

.chat-log {
	> .dashboardChatLog {
		word-break: break-word;

		> .message {
			cursor: pointer;
			padding: 2px 4px;
			position: relative;
			img { max-width: 100%; }
			&:hover { background: rgba(255, 255, 255, .1); }
			a {
				color: $vaporwave-green;
				text-decoration: underline;
				word-break: break-all;
				&:hover { color: lighten($vaporwave-green, 10%); }
			}
			&.highlighted a { color: darken($vaporwave-green, 35%); }
			&.gift, &.product, &.subscription {
				background: $gold;
				color: $gold-text;
				margin: 4px;
				border-radius: 4px;
				padding: 4px;
				&:hover { background: darken($gold, 5%); }
			}
			&.highlighted {
				background: $vaporwave-white;
				color: $dark;
				&:hover { background: darken($vaporwave-white, 5%); }
			}
			> .avatar, > .service {
				width: 24px;
				height: 24px;
				border-radius: 12px;
				object-fit: cover;
				overflow: hidden;
			}
			> .name:after { content: ' ' }
			&.chat > .name:after { content: ': ' }
			&.chat.gift > .name:after { content: ' ' }
			&.chat.product > .name:after { content: ' ' }
			> .content {
				display: inline;
				> img {
					max-width: 80px;
					max-height: 80px;
					&.emote { max-width: 32px; max-height: 32px; }
				}
			}
			&.self { color: fade-out($vaporwave-white, .4); }
			&.owner { color: $vaporwave-green; }
			> div.menubtn {
				display: none;
				position: absolute;
				font-size: 15px;
				right: 0; top: 0; bottom: 0;
				background: transparent;
				line-height: 100%;
				align-items: center;
				padding: 4px 24px;
				text-align: center;
				color: white;
				&:after { content: '⊕ Show'; }
				backdrop-filter: blur(3px);
				z-index: 1;
			}
			&.highlighted > div.menubtn {
				color: $dark;
				&:after { content: '⊖ Hide'; }
			}
			&:hover > div.menubtn { display: inline-flex; }
			&:hover > div.menubtn:hover { background: rgba(255, 255, 255, .1); }
			> .alert-status-badge {
				position: absolute;
				right: 4px;
				top: 50%;
				transform: translateY(-50%);
				font-size: 10px;
				padding: 1px 5px;
				border-radius: 3px;
				pointer-events: none;
				background: rgba(0,0,0,.5);
				color: #ccc;
				&[data-status="playing"] { background: rgba(52,152,219,.7); color: #fff; }
				&[data-status="played"]  { background: rgba(39,174, 96,.6); color: #fff; }
				&[data-status="error"]   { background: rgba(231,76, 60,.7); color: #fff; }
				&[data-status="paused"]  { background: rgba(243,156, 18,.7); color: #fff; }
				&[data-status="skipped"] { background: rgba(149,165,166,.6); color: #fff; }
			&[data-status="ignored"] { background: rgba(149,165,166,.35); color: #ccc; }
			}
		}
	}

	> .chatLogScrollToBottom {
		position: absolute;
		bottom: 3.7em;
		right: 0;
		left: 0;
		text-align: center;
		pointer-events: none;
		> button { pointer-events: auto; }
	}
}
</style>
